import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styles from '../../styles/Team.module.css';

const MonetizationCharts = ({ data, timeRange }) => {
  // Top 5 earners for the chart
  const topEarners = data.slice(0, 5);
  
  // RPM distribution buckets
  const rpmBuckets = {
    'Low (< $1)': data.filter(page => page.video_rpm < 1).length,
    'Medium ($1-$5)': data.filter(page => page.video_rpm >= 1 && page.video_rpm < 5).length,
    'High ($5-$10)': data.filter(page => page.video_rpm >= 5 && page.video_rpm < 10).length,
    'Premium (> $10)': data.filter(page => page.video_rpm >= 10).length
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatRPM = (rpm) => {
    return `$${rpm.toFixed(2)}`;
  };

  return (
    <div className={styles.monetizationChartsContainer}>
      <div className={styles.monetizationChartsHeader}>
        <h3>
          <FontAwesomeIcon icon="chart-bar" style={{ marginRight: '8px' }} />
          Monetization Analytics
        </h3>
        <p>Performance insights and trends for {timeRange === 1 ? 'yesterday' : `last ${timeRange} days`}</p>
      </div>

      <div className={styles.chartsGrid}>
        {/* Top Earners Chart */}
        <div className={styles.chartCard}>
          <h4>
            <FontAwesomeIcon icon="crown" style={{ marginRight: '6px' }} />
            Top 5 Earners
          </h4>
          <div className={styles.topEarnersChart}>
            {topEarners.map((page, index) => (
              <div key={page.page_id} className={styles.topEarnerItem}>
                <div className={styles.earnerRank}>
                  <span className={styles.rankNumber}>#{index + 1}</span>
                </div>
                <div className={styles.earnerInfo}>
                  <div className={styles.earnerName}>{page.page_name}</div>
                  <div className={styles.earnerStats}>
                    <span className={styles.earnings}>{formatCurrency(page.total_approximate_earnings)}</span>
                    <span className={styles.rpm}>• {formatRPM(page.video_rpm)} RPM</span>
                  </div>
                </div>
                <div className={styles.earnerViews}>
                  {page.total_video_views.toLocaleString()} views
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* RPM Distribution Chart */}
        <div className={styles.chartCard}>
          <h4>
            <FontAwesomeIcon icon="chart-pie" style={{ marginRight: '6px' }} />
            RPM Distribution
          </h4>
          <div className={styles.rpmDistributionChart}>
            {Object.entries(rpmBuckets).map(([bucket, count]) => (
              <div key={bucket} className={styles.rpmBucket}>
                <div className={styles.rpmBucketLabel}>{bucket}</div>
                <div className={styles.rpmBucketBar}>
                  <div 
                    className={styles.rpmBucketFill}
                    style={{ 
                      width: `${(count / data.length) * 100}%`,
                      backgroundColor: getRPMColor(bucket)
                    }}
                  />
                </div>
                <div className={styles.rpmBucketCount}>{count} pages</div>
              </div>
            ))}
          </div>
        </div>

        {/* Performance Metrics */}
        <div className={styles.chartCard}>
          <h4>
            <FontAwesomeIcon icon="chart-bar" style={{ marginRight: '8px' }} />
            Performance Metrics
          </h4>
          <div className={styles.performanceMetrics}>
            <div className={styles.metricRow}>
              <span className={styles.metricLabel}>Avg Earnings per Page:</span>
              <span className={styles.metricValue}>
                {formatCurrency(data.reduce((sum, page) => 
                  sum + parseFloat(page.total_approximate_earnings), 0
                ) / data.length)}
              </span>
            </div>
            <div className={styles.metricRow}>
              <span className={styles.metricLabel}>Median RPM:</span>
              <span className={styles.metricValue}>
                {formatRPM(calculateMedianRPM(data))}
              </span>
            </div>
            <div className={styles.metricRow}>
              <span className={styles.metricLabel}>Total Video Views:</span>
              <span className={styles.metricValue}>
                {data.reduce((sum, page) => sum + parseInt(page.total_video_views), 0).toLocaleString()}
              </span>
            </div>
            <div className={styles.metricRow}>
              <span className={styles.metricLabel}>Total Impressions:</span>
              <span className={styles.metricValue}>
                {data.reduce((sum, page) => sum + parseInt(page.total_impressions), 0).toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to get RPM color based on bucket
const getRPMColor = (bucket) => {
  switch (bucket) {
    case 'Low (< $1)': return '#ef4444';
    case 'Medium ($1-$5)': return '#f59e0b';
    case 'High ($5-$10)': return '#10b981';
    case 'Premium (> $10)': return '#8b5cf6';
    default: return '#6b7280';
  }
};

// Helper function to calculate median RPM
const calculateMedianRPM = (data) => {
  const rpms = data.map(page => page.video_rpm).filter(rpm => rpm > 0).sort((a, b) => a - b);
  if (rpms.length === 0) return 0;
  
  const mid = Math.floor(rpms.length / 2);
  return rpms.length % 2 === 0 
    ? (rpms[mid - 1] + rpms[mid]) / 2 
    : rpms[mid];
};

export default MonetizationCharts; 